import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { createSearchParams, useLocation } from 'react-router-dom';
import { omitRestEmpty } from '@1c/utils/lib/restApi';
import { CheckBox } from '@1c/ui';
import { useSearchParams } from 'src/utils/useSearchParams'
import { useNavigate } from 'src/utils/useNavigate';
import { postStartEmailConfirmationAPI, putAccountProfileAPI } from 'src/serverApi/account';
import { FORM_ERROR } from 'final-form';
import useWithAuthorities from 'src/utils/useWithAuthorities';
import permissions from 'src/constants/permissions';
import { FinalField, FinalForm } from 'src/components/FinalForm';
import Button from 'src/components/Button';
import InputField from 'src/components/InputField';
import Text from 'src/components/Text';
import Link from 'src/components/Link';
import { AuthorizationContext } from 'src/contexts/authorizationContext';
import routes from 'src/constants/routes';
import EmailInput from '../EmailInput';
import validate from './validate';
import styles from './styles.module.scss';

type Props = {
  accountProfile: any;
};

const Form: React.FC<Props> = ({ accountProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authorizationState = useContext(AuthorizationContext);
  const userId = authorizationState?.userId;
  const isEmptyAccountProfile = !accountProfile.firstName && !accountProfile.secondName
    && !accountProfile.middleName && !accountProfile.email;
  const [isLoading, setIsLoading] = useState(false);
  const [personalDataAgreementChecked, setPersonalDataAgreementChecked] = useState(false);
  const withAccountManagementAuthorities = useWithAuthorities([permissions.accountManagement]);
  
  const handlePersonalDataAgreementClick = (value: any) => {
    setPersonalDataAgreementChecked(value?.currentTarget?.checked);
  };

  const handleSubmit = async (values: any) => {
    const searchParamsValues = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      return { ...acc, [key]: value === null || value === undefined ? '' : value };
    }, {});
    const returnUrlSearchParams = createSearchParams(searchParamsValues).toString();
    
    try {
      setIsLoading(true);
      await withAccountManagementAuthorities(async () => {
        await putAccountProfileAPI(omitRestEmpty(values));
        const isEmailChanged = values.email !== accountProfile?.email;
        if (isEmailChanged) {
          await postStartEmailConfirmationAPI();
          toast.success('На почту отправлена ссылка для подтверждения адреса');
        } else {
          toast.success('Изменения сохранены');
        }
        navigate(routes.agent);
      }, `${location.pathname}?${returnUrlSearchParams}`);
    } catch (error) {
      toast.error('Произошла ошибка при попытке сохранить изменения профиля');
      return { [FORM_ERROR]: 'Произошла ошибка при попытке сохранить изменения профиля' };
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FinalForm
      onSubmit={handleSubmit}
      submitting={isLoading}
      pristine={!searchParams.toString()}
      initialValues={{
        firstName: searchParams.has('firstName') ? searchParams.get('firstName') : accountProfile?.firstName,
        secondName: searchParams.has('secondName') ? searchParams.get('secondName') : accountProfile?.secondName,
        middleName: searchParams.has('middleName') ? searchParams.get('middleName') : accountProfile?.middleName,
        email: searchParams.has('email') ? searchParams.get('email') : accountProfile?.email,
      }}
      validate={validate}
    >
      {({ submitting, pristine }: { submitting: any; pristine: any }) => {
        return (
          <div>
            <InputField
              label="Телефон"
              value={userId}
              disabled
              className={styles['input']}
            />
            <Link to={routes.changePassword} className={styles['change-password-link']}>Изменить пароль</Link>
            <FinalField name="secondName">
              {({ value, onChange, error }: { value: any; onChange: any; error: string }) => (
                <InputField
                  labelRequired
                  label="Фамилия"
                  value={value}
                  onChange={onChange}
                  error={error}
                  className={styles['input']}
                />
              )}
            </FinalField>
            <FinalField name="firstName">
              {({ value, onChange, error }: { value: any; onChange: any; error: string }) => (
                <InputField
                  labelRequired
                  label="Имя"
                  value={value}
                  onChange={onChange}
                  error={error}
                  className={styles['input']}
                />
              )}
            </FinalField>
            <FinalField name="middleName">
              {({ value, onChange }: { value: any; onChange: any }) => (
                <InputField
                  label="Отчество"
                  value={value}
                  onChange={onChange}
                  className={styles['input']}
                />
              )}
            </FinalField>
            <FinalField name="email">
              {({ value, onChange, error }: { value: any; onChange: any; error: string }) => (
                <EmailInput
                  value={value}
                  onChange={onChange}
                  error={error}
                  accountProfile={accountProfile}
                  />
              )}
            </FinalField>
            {isEmptyAccountProfile && (
              <CheckBox
                className={styles['checkbox']}
                checked={personalDataAgreementChecked}
                onChange={handlePersonalDataAgreementClick}
              >
                <Text type="span" muted small>Согласие </Text>
                <Link to={routes.offer} className={styles['link']} target="_blank">
                  на обработку персональных данных
                </Link>
              </CheckBox>
            )}
            <Button
              disabled={submitting || pristine || (isEmptyAccountProfile && !personalDataAgreementChecked)}
              width="available"
              type="submit"
              className={styles['button']}
            >
              Сохранить
            </Button>
          </div>
        );
      }}
    </FinalForm>
  );
};

export default Form;
